import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";

const FALLBACK = { USD: 1.0, EUR: 0.92, CHF: 0.88, HKD: 7.82, AED: 3.67, IDR: 15850.0, THB: 34.5 };
const SYMBOLS = { USD: "$", EUR: "€", CHF: "CHF ", HKD: "HK$", AED: "AED ", IDR: "Rp ", THB: "฿" };
const ZERO_DECIMAL = new Set(["IDR", "JPY", "KRW", "VND"]);
const SUPPORTED = Object.keys(FALLBACK);

const Ctx = createContext(null);

const PREF_KEY = "opik_currency_pref_set";

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => localStorage.getItem("opik_currency") || "USD");
  const [rates, setRates] = useState(FALLBACK);
  // When the admin disables auto conversion in /admin/pricing, every price
  // renders in its source (base) currency regardless of the visitor's pick.
  const [autoConvertEnabled, setAutoConvertEnabled] = useState(true);
  const [prefSet, setPrefSet] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(PREF_KEY) === "1";
  });

  useEffect(() => {
    api.get("/fx/rates").then(({ data }) => {
      if (data?.rates) setRates(data.rates);
    }).catch(() => {});
    api.get("/pricing/config").then(({ data }) => {
      if (data && typeof data.auto_convert_enabled === "boolean") {
        setAutoConvertEnabled(data.auto_convert_enabled);
      }
    }).catch(() => {});
  }, []);

  const change = useCallback((c, opts = {}) => {
    if (!c) return;
    setCurrency(c);
    localStorage.setItem("opik_currency", c);
    if (opts.fromUser !== false) {
      try { localStorage.setItem(PREF_KEY, "1"); } catch (e) { /* ignore */ }
      setPrefSet(true);
    }
  }, []);

  const markPrefSet = useCallback(() => {
    try { localStorage.setItem(PREF_KEY, "1"); } catch (e) { /* ignore */ }
    setPrefSet(true);
  }, []);

  /**
   * Convert an amount stored in `fromCurrency` into the user's selected
   * display currency. We bridge via USD: amount_usd = amount / rates[from];
   * displayed = amount_usd × rates[to].
   *
   * When `autoConvertEnabled` is FALSE (set via /api/pricing/config), we
   * skip conversion entirely and render in the source currency.
   */
  const convert = useCallback((amount, fromCurrency = "USD") => {
    if (!autoConvertEnabled) return Math.round(Number(amount) || 0);
    const fromRate = rates[fromCurrency] || 1;
    const toRate = rates[currency] || 1;
    const v = (Number(amount) / fromRate) * toRate;
    return Math.round(v);
  }, [rates, currency, autoConvertEnabled]);

  const format = useCallback((amount, fromCurrency = "USD") => {
    const displayCcy = autoConvertEnabled ? currency : (fromCurrency || "USD");
    const fromRate = rates[fromCurrency] || 1;
    const toRate = rates[displayCcy] || 1;
    const v = autoConvertEnabled ? (Number(amount) / fromRate) * toRate : Number(amount);
    const rounded = Math.round(v);
    const locale = displayCcy === "IDR" ? "id-ID" : displayCcy === "THB" ? "th-TH" : "en-US";
    return `${SYMBOLS[displayCcy] || ""}${Intl.NumberFormat(locale).format(rounded)}`;
  }, [rates, currency, autoConvertEnabled]);

  // Stable provider value — re-renders consumers only when state actually changes.
  const value = useMemo(
    () => ({ currency, change, markPrefSet, prefSet, rates, convert, format, supported: SUPPORTED }),
    [currency, change, markPrefSet, prefSet, rates, convert, format]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useCurrency = () => useContext(Ctx);
